import { useReactiveVar } from '@apollo/client'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { useNodeMapShallowSelector } from 'components/WorkspaceEditor/features/keyOperations/store/NodeMapStore'
import { isMOPTArticle } from 'components/WorkspaceEditor/features/moptArticleRestriction'
import { useSelectedMainMenuNode } from 'components/WorkspaceEditor/HoveredItemStore'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import { getSortedSiblings } from 'components/WorkspaceEditor/utils'
import { useContentItemViewNodes } from 'components/WorkspaceEditor/Workspace2Store'
import { useMemo } from 'react'

/**
 * A custom hook that determines whether the "Move Up" and "Move Down" actions
 */
export const useMoveActionAvailability = () => {
    const contentItemViewNodes = useContentItemViewNodes()
    const selectedMainMenuNode = useSelectedMainMenuNode()
    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )
    const { isSaveOperationInProgress } = useNodeMapShallowSelector(
        (state) => ({
            isSaveOperationInProgress: state.isSaveOperationInProgress,
        })
    )

    /**
     * Check if the selected node is a MOPT (Manufacturer-sourced Product Type) article
     * Must be an Article with isCreatedFromPremiumPlus and partType === 'Products'
     */
    const isMOPT = useMemo(() => {
        if (!selectedMainMenuNode) return false
        return (
            selectedMainMenuNode.textType === 'Article' &&
            isMOPTArticle(selectedMainMenuNode)
        )
    }, [selectedMainMenuNode])

    /**
     * Gets a list of sibling nodes, sorted according to workspace logic.
     */
    const siblingNodesSortedByOrder: Partial<
        ContentItemViewNodesType
    >[] = useMemo(() => {
        return getSortedSiblings(
            contentItemViewNodes,
            selectedMainMenuNode?.dtvParentId,
            selectedMainMenuNode?.dtvContentItemParentId
        )
    }, [contentItemViewNodes, selectedMainMenuNode])

    /**
     * Finds the index of the currently selected node among its siblings.
     */
    const selectedNodeSiblingIndex: number = useMemo(() => {
        if (!selectedMainMenuNode) return -1
        return siblingNodesSortedByOrder.findIndex(
            (item) => item.id === selectedMainMenuNode?.id
        )
    }, [siblingNodesSortedByOrder, selectedMainMenuNode?.id])

    /**
     * Check if moving up would violate MOPT adjacency rules
     * Moving up swaps with previous sibling - if previous is MOPT,
     * after swap current node would be after MOPT (invalid)
     */
    const wouldViolateMOPTOnMoveUp = useMemo(() => {
        if (!selectedMainMenuNode || selectedMainMenuNode.textType !== 'Article') {
            return false
        }
        if (isMOPT) return false // MOPT articles can't move anyway
        if (selectedNodeSiblingIndex <= 0) return false // Already at top

        const previousSibling = siblingNodesSortedByOrder[selectedNodeSiblingIndex - 1]
        return previousSibling?.textType === 'Article' && isMOPTArticle(previousSibling)
    }, [selectedMainMenuNode, isMOPT, selectedNodeSiblingIndex, siblingNodesSortedByOrder])

    /**
     * Check if moving down would violate MOPT adjacency rules
     * Moving down swaps with next sibling - if next is MOPT,
     * after swap current node would be after MOPT (invalid)
     */
    const wouldViolateMOPTOnMoveDown = useMemo(() => {
        if (!selectedMainMenuNode || selectedMainMenuNode.textType !== 'Article') {
            return false
        }
        if (isMOPT) return false // MOPT articles can't move anyway
        if (selectedNodeSiblingIndex === -1 ||
            selectedNodeSiblingIndex === siblingNodesSortedByOrder.length - 1) {
            return false // Already at bottom
        }

        const nextSibling = siblingNodesSortedByOrder[selectedNodeSiblingIndex + 1]
        return nextSibling?.textType === 'Article' && isMOPTArticle(nextSibling)
    }, [selectedMainMenuNode, isMOPT, selectedNodeSiblingIndex, siblingNodesSortedByOrder])
    const canMoveUp = useMemo(
        () =>
            !selectedProjectElementNode?.isInProject ||
            selectedNodeSiblingIndex <= 0 ||
            isSaveOperationInProgress ||
            isMOPT ||
            wouldViolateMOPTOnMoveUp,
        [
            selectedProjectElementNode?.isInProject,
            selectedNodeSiblingIndex,
            isSaveOperationInProgress,
            isMOPT,
            wouldViolateMOPTOnMoveUp,
        ]
    )

    const canMoveDown = useMemo(
        () =>
            !selectedProjectElementNode?.isInProject ||
            selectedNodeSiblingIndex === siblingNodesSortedByOrder.length - 1 ||
            selectedNodeSiblingIndex === -1 ||
            isSaveOperationInProgress ||
            isMOPT ||
            wouldViolateMOPTOnMoveDown,
        [
            selectedProjectElementNode?.isInProject,
            selectedNodeSiblingIndex,
            siblingNodesSortedByOrder.length,
            isSaveOperationInProgress,
            isMOPT,
            wouldViolateMOPTOnMoveDown,
        ]
    )

    return {
        canMoveUp,
        canMoveDown,
    }
}
